import { Color, Radius, Spacing } from 'zens-storybook'
import React, { Suspense, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { ApiError, Draft, LocationData, useLocationMutation, useStore } from 'zens-store'
import { useRouter, useNumberParams } from 'zens-router'
import { LayoutHeader, LocationForm, LocationHeader, NavigationDelete } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
})

const LocationEdit = () => {
  const [confirmDelete, setConfirmDelete] = useState<Draft<LocationData>>()
  const [errors, setErrors] = useState<Record<string, string>>()
  const router = useRouter()
  const { message } = useStore()
  const [{ companyId, locationId }] = useNumberParams()
  const mutation = useLocationMutation({ companyId })
  const { t } = useTranslation()

  const save = async (draft: Draft<LocationData>) => {
    setErrors({})
    try {
      await mutation.update(draft)
      message.add({
        message: t('Location was updated successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`LOCATION: Location failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Location could not be saved')}. ${errorMessage}`,
          status: 'error',
        })
      }
      return
    }

    router.push(`/companies/${companyId}/locations/${locationId}`)
  }

  const remove = async () => {
    try {
      await mutation.delete(confirmDelete!)
      message.add({
        message: t('Location was deleted successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage } = error as Error
      console.debug(`LOCATION: Location deletion failed: ${errorMessage}`)
      message.add({
        message: `${t('Location could not be deleted')}. ${errorMessage}`,
        status: 'error',
      })
      return
    }

    router.push(`/companies/${companyId}/detail`)
  }

  return (
    <View style={styles.container}>
      <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
        <LocationHeader companyId={companyId} locationId={locationId} breadcrumbItems={[{ title: t('Edit') }]} />
      </Suspense>
      <View style={styles.content}>
        <Suspense fallback={<LocationForm.Skeleton />}>
          <LocationForm
            companyId={companyId}
            locationId={locationId}
            onSave={save}
            cancelHref={`/companies/${companyId}/locations/${locationId}`}
            onDelete={async (draft) => setConfirmDelete(draft)}
            errors={errors}
          />
        </Suspense>
        <NavigationDelete
          entity={t('Location')}
          visible={!!confirmDelete}
          onClose={() => setConfirmDelete(undefined)}
          onDelete={remove}
        />
      </View>
    </View>
  )
}

export default LocationEdit
